import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { useAuthStore } from '../store/authStore';

export default function Pricing() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const pricingTiers = [
    {
      name: 'Starter Pack',
      credits: 5,
      price: 4.99,
      pricePerCredit: 0.99,
      popular: false,
      features: [
        '5 story credits',
        'All story types available',
        'High-quality audio generation',
        'Download & keep forever',
        'British accent narration',
        'Credits never expire',
      ],
    },
    {
      name: 'Family Pack',
      credits: 15,
      price: 12.99,
      pricePerCredit: 0.87,
      popular: true,
      savings: 'Save 12%',
      features: [
        '15 story credits',
        'All story types available',
        'High-quality audio generation',
        'Download & keep forever',
        'British accent narration',
        'Credits never expire',
        'Best value for families',
      ],
    },
    {
      name: 'Premium Pack',
      credits: 30,
      price: 23.99,
      pricePerCredit: 0.80,
      popular: false,
      savings: 'Save 20%',
      features: [
        '30 story credits',
        'All story types available',
        'High-quality audio generation',
        'Download & keep forever',
        'British accent narration',
        'Credits never expire',
        'Maximum savings',
      ],
    },
  ];

  const handleGetStarted = () => {
    if (isAuthenticated) {
      // If logged in, go to dashboard (where they can purchase credits)
      navigate('/dashboard');
    } else {
      // If not logged in, go to register
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-background-cream">
      {/* Header Navigation */}
      <header className="bg-white shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/')}>
              <Logo size="md" />
              <span className="text-xl font-display font-bold text-primary-500">
                StorySprout
              </span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900 font-semibold"
              >
                Home
              </button>
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="btn-primary px-6 py-2"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="btn-outline px-6 py-2"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="btn-primary px-6 py-2"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-500 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            Purchase story credits and use them whenever you need. Credits never expire, so you can create stories at your own pace.
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/95 backdrop-blur-sm border-2 border-white rounded-xl shadow-lg">
            <span className="text-2xl">🎁</span>
            <span className="text-primary-600 font-semibold">
              Get 3 free credits when you sign up!
            </span>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingTiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative bg-white rounded-3xl shadow-soft-lg p-8 ${
                tier.popular
                  ? 'ring-4 ring-primary-500 transform scale-105'
                  : 'hover:shadow-xl transition-shadow'
              }`}
            >
              {/* Popular Badge */}
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    MOST POPULAR
                  </div>
                </div>
              )}

              {/* Savings Badge */}
              {tier.savings && !tier.popular && (
                <div className="absolute -top-3 -right-3">
                  <div className="bg-accent-500 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg">
                    {tier.savings}
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">
                  {tier.name}
                </h3>
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-5xl font-display font-bold text-primary-600">
                    ${tier.price}
                  </span>
                </div>
                <div className="text-gray-600 mb-1">
                  <span className="text-3xl font-bold text-emerald-600">
                    {tier.credits}
                  </span>{' '}
                  <span className="text-lg">credits</span>
                </div>
                <p className="text-sm text-gray-500">
                  ${tier.pricePerCredit.toFixed(2)} per credit
                </p>
              </div>

              {/* Features List */}
              <ul className="space-y-4 mb-8">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={handleGetStarted}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                  tier.popular
                    ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:shadow-lg transform hover:-translate-y-1'
                    : 'bg-gray-100 text-primary-600 hover:bg-gray-200'
                }`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>

        {/* How Credits Work */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-center text-gray-900 mb-8">
            How Credits Work
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card text-center">
              <div className="text-4xl mb-4">📖</div>
              <h3 className="font-display font-bold text-lg text-emerald-500 mb-2">
                Short Story
              </h3>
              <p className="text-gray-700 mb-2">500 words • ~3 mins</p>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600">
                <span className="text-xl font-display font-bold text-white">1</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">credit</p>
            </div>

            <div className="card text-center">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="font-display font-bold text-lg text-emerald-500 mb-2">
                Medium Story
              </h3>
              <p className="text-gray-700 mb-2">1,000 words • ~7 mins</p>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600">
                <span className="text-xl font-display font-bold text-white">2</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">credits</p>
            </div>

            <div className="card text-center">
              <div className="text-4xl mb-4">📕</div>
              <h3 className="font-display font-bold text-lg text-emerald-500 mb-2">
                Long Story
              </h3>
              <p className="text-gray-700 mb-2">2,000 words • ~15 mins</p>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600">
                <span className="text-xl font-display font-bold text-white">4</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">credits</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="card">
              <h3 className="font-bold text-lg text-gray-900 mb-2">
                Do credits expire?
              </h3>
              <p className="text-gray-700">
                No! Your credits never expire. Purchase them once and use them whenever you need to create a story.
              </p>
            </div>

            <div className="card">
              <h3 className="font-bold text-lg text-gray-900 mb-2">
                Can I get a refund?
              </h3>
              <p className="text-gray-700">
                We offer a 30-day money-back guarantee. If you're not satisfied with your purchase, contact us for a full refund.
              </p>
            </div>

            <div className="card">
              <h3 className="font-bold text-lg text-gray-900 mb-2">
                How long does it take to generate a story?
              </h3>
              <p className="text-gray-700">
                Most stories are ready within 2-5 minutes. You'll receive a notification when your audiobook is ready to download.
              </p>
            </div>

            <div className="card">
              <h3 className="font-bold text-lg text-gray-900 mb-2">
                Can I download and keep the audiobooks?
              </h3>
              <p className="text-gray-700">
                Yes! Once your story is generated, you can download the audio file and keep it forever. Listen on any device, anytime.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-gradient-to-r from-primary-500 to-secondary-500 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-display font-bold text-white mb-4">
            Ready to Create Magic?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start with 3 free credits and create your first personalized audiobook today.
          </p>
          <button
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
            className="bg-white text-primary-600 font-bold px-10 py-4 rounded-xl text-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
          >
            {isAuthenticated ? 'Go to Dashboard' : 'Sign Up Free'}
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-primary-500 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg font-semibold mb-2">
            Built with care for children and families
          </p>
          <p className="text-sm opacity-80">
            COPPA & GDPR Compliant | Secure & Private
          </p>
        </div>
      </footer>
    </div>
  );
}
